import { Information } from '@carbon/icons-react'
import { Flex, Text } from '@chakra-ui/react'

import RSVDetailView from 'routes/apps/rsv/Candidates'

import { Panel } from 'components'

import { SortOrders } from 'enums/SortOrders'

import { ApprovalsModel } from 'config/common/baseModel/Approvals'
import {
  RespiratoryCandidatesAllCombinedModel,
  RespiratoryCombinationVaccinesModel,
  RespiratoryVaccinesModel,
  RespiratoryTreatmentsModel,
} from 'config/common/baseModel/respiratory360/RespiratoryCandidates'
import { RespiratoryToolMetaVaccineEfficacyModel } from 'config/common/baseModel/respiratory360/RespiratoryToolMetaVaccineEfficacy'

import {
  blackListColumn,
  compose,
  renameColumn,
  updateColumnAttributes,
  whiteListColumn,
} from 'utils/overrideModel'
import { CandidateRankingModel } from 'config/common/baseModel'

const PhaseSortingObject = {
  sortOrderObject: [
    'null',
    'Validation',
    'Not Applicable',
    'Unknown',
    'Inactive',
    'Discontinued',
    'Paused',
    'Product Launch',
    'Approved',
    'Phase IV',
    'Preclinical',
    'Phase 0',
    'Phase I',
    'Phase I/II',
    'Phase II',
    'Phase II/III',
    'Phase III',
    'Phase III/IV',
  ],
}

export const RSVCandidatesVaccineModel = compose<
  typeof RespiratoryVaccinesModel
>(
  blackListColumn(['vaccineValency', 'seasonalOrPandemic', 'targetViralStrain'])
)({
  ...RespiratoryVaccinesModel,
  renderDetailView: RSVDetailView,
})

export const RSVCandidatesTreatmentModel = compose<
  typeof RespiratoryTreatmentsModel
>(blackListColumn(['designationB']))({
  ...RespiratoryTreatmentsModel,
  renderDetailView: RSVDetailView,
})

export const RSVCombinationVaccineModel = {
  ...RespiratoryCombinationVaccinesModel,
  renderDetailView: RSVDetailView,
}

export const RSVCandidatesAllCombinedModel = compose<
  typeof RespiratoryCandidatesAllCombinedModel
>(blackListColumn(['seasonalOrPandemic', 'targetViralStrain']))({
  ...RespiratoryCandidatesAllCombinedModel,
  renderDetailView: RSVDetailView,
})

export const RSVCandidatesCombinationVaccinesModel = compose<
  typeof CandidateRankingModel
>(
  whiteListColumn([
    'diseaseName',
    'name',
    'alternativeNames',
    'latestPhaseManual',
    // TODO 'vaccineGroupTypeSplit',
    'targetPopulation',
    'sponsors',
    'affiliates',
    'routeOfAdministration',
    'latestPhaseManualSource',
  ]),
  updateColumnAttributes('latestPhaseManual', PhaseSortingObject)
)({
  ...CandidateRankingModel,
  name: 'Combination vaccines',
  renderDetailView: RSVDetailView,
  defaultSortOrder: SortOrders.DESC,
  defaultSortKey: 'latestPhaseManual',
})

export const RSVToolMetaVaccineEfficacyModel = compose<
  typeof RespiratoryToolMetaVaccineEfficacyModel
>(
  whiteListColumn([
    'candidateNameFromVaccineCandidate',
    'companies',
    'phasesCleanFromClinicalTrialId',
    'trialType',
    'achievedPrimaryEndpoint',
    'criteriaForPrimaryEndpoint',
    'studyPopulationType',
    'clinicalTrialId',
    'genPop',
    'efficacyEffectiveness',
    'confidenceIntervalOfEfficacy',
    'adverseEventsMildModerate',
    'adverseEventsSevere',
    'trialSize',
    'numberDoses',
    'dosingInterval',
    'dose',
    'numberOfConfirmedCases',
    'numberOfCasesInVaccinatedGroup',
    'numberOfCasesOnPlaceboGroup',
    'trialLocations',
    'articleLink',
    'dataSource',
  ])
)({
  ...RespiratoryToolMetaVaccineEfficacyModel,
  name: 'Vaccine Efficacy',
  description: () => (
    <Panel
      px={2}
      py={1}
      my={2}
      maxW='600px'
      border='1px solid'
      borderColor='gray3'
      borderRadius='8px'
      mb={2}
      shadow={'sm'}
    >
      <Flex gap={1}>
        <Information style={{ marginTop: '2px' }} />
        <Text fontSize={'13px'}>
          These vaccines have not been compared in head to heads and thus,
          differences should be interpreted with caution.
        </Text>
      </Flex>
    </Panel>
  ),
  excludeGeneratedCharts: true,
})

export const RSVToolMetaNonVaccineEfficacyModel = compose<
  typeof RespiratoryToolMetaVaccineEfficacyModel
>(
  whiteListColumn([
    'candidateNameFromTreatmentCandidate',
    'manualLatestPhaseFromTreatmentCandidate',
    'linkCompaniesFromTreatmentCandidate',
    'trialType',
    'trialPhase',
    'achievedPrimaryEndpoint',
    'treatmentTimingFromTreatmentCandidate',
    'treatmentTypeFromTreatmentCandidate',
    'directVsHostTargetedFromTreatmentCandidate',
    'studyPopulationType',
    'clinicalTrialId',
    'genPop',
    'efficacyResults',
    'efficacyRelativeReductionInHospitalisation',
    'efficacyAdditionalSAndSScoreReduction',
    'efficacyAdditionalReductionViralLoad',
    'pooledEfficacyRelativeReductionInHospitalisation',
    'efficacyRelativeReductionInInfection',
    'efficacyAucMeanViralLoad',
    'efficacyAucMeanSymptomScore',
    'adverseReactions',
    'safetySevereAdverseReactions',
    'safetyGeGrade3',
    'safetyUpperRespiratoryInfection',
    'safetyPharyngitis',
    'safetyRhinitis',
    'safetyOtitisMedia',
    'safetyPain',
    'safetyHeadache',
    'safetyRash',
    'safetyHernia',
    'safetyGastrointestinalDisorders',
    'safetyDiarrhoea',
    'safetyDeath',
    'dataSource',
  ]),
  renameColumn('candidateNameFromVaccineCandidate', 'Non-Vaccine'),
  renameColumn('safetySevereAdverseReactions', 'Serious Adverse Reactions'),
  updateColumnAttributes('studyPopulationType', { width: 180 })
)({
  ...RespiratoryToolMetaVaccineEfficacyModel,
  displayKey: 'candidateNameFromTreatmentCandidate',
  searchField: 'candidateNameFromTreatmentCandidate',
  excludeGeneratedCharts: true,
})

export const RsvApprovalsVaccinesModel = compose<typeof ApprovalsModel>(
  whiteListColumn([
    'candidate',
    'sponsors',
    'targetPopulations',
    'type',
    'area',
    'regulatoryOrganisation',
    'associatedDocumentUrl',
    'approvalDate',
  ])
)({
  ...ApprovalsModel,
  name: 'Regulatory',
  defaultSortKey: 'approvalDate',
})
export const RsvApprovalsNonVaccinesModel = compose<typeof ApprovalsModel>(
  whiteListColumn([
    'candidate',
    'technologyType',
    'sponsors',
    'targetPopulations',
    'indication',
    'patientSetting',
    'type',
    'area',
    'regulatoryOrganisation',
    'associatedDocumentUrl',
    'approvalDate',
  ])
)({
  ...ApprovalsModel,
  name: 'Regulatory',
})
